import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, IndianRupee, RefreshCw, Search, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Order } from '../types';

const statusOptions = ['', 'Assigned', 'Service InProgress', 'Service Done', 'Payment Received', 'Cancelled'];

const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString('en-IN') : '-';
const formatAmount = (amount?: number) => `Rs ${Number(amount || 0).toLocaleString('en-IN')}`;
const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today'>('all');
  const pageSize = 10;

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.searchMyOrders({
        page,
        pageSize,
        searchTerm,
        status: status || undefined,
        startDate: dateFilter === 'today' ? formatDateInput(new Date()) : undefined,
        endDate: dateFilter === 'today' ? formatDateInput(new Date()) : undefined,
      });

      setOrders(response.entity?.items || []);
      setTotalCount(response.entity?.totalCount || 0);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, page, searchTerm, status]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const totals = useMemo(() => {
    return orders.reduce(
      (summary, order) => ({
        totalAmount: summary.totalAmount + Number(order.finalAmount || 0),
        serviceCost: summary.serviceCost + Number(order.totalServiceCost || 0),
      }),
      { totalAmount: 0, serviceCost: 0 }
    );
  }, [orders]);

  return (
    <div>
      <div className="page-heading">
        <div>
          <h1>My Orders</h1>
          <p>Read-only view of orders assigned to your technician mapping.</p>
        </div>
        <button
          type="button"
          className="refresh-button"
          onClick={loadOrders}
          disabled={loading}
          title="Refresh orders"
          aria-label="Refresh orders"
        >
          <RefreshCw size={16} className={loading ? 'spin-icon' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={17} />
          <input value={searchTerm} onChange={(event) => { setPage(1); setSearchTerm(event.target.value); }} placeholder="Search customer, mobile, order, device" />
        </div>
      </div>

      <div className="quick-filters">
        <button
          className={dateFilter === 'all' ? 'active' : ''}
          onClick={() => { setPage(1); setDateFilter('all'); }}
        >
          All Orders
        </button>
        <button
          className={dateFilter === 'today' ? 'active' : ''}
          onClick={() => { setPage(1); setDateFilter('today'); }}
        >
          Today's Orders
        </button>
      </div>

      <div className="status-filters" aria-label="Order status filter">
        {statusOptions.map((option) => (
          <button
            key={option || 'all'}
            className={status === option ? 'active' : ''}
            onClick={() => { setPage(1); setStatus(option); }}
          >
            {option || 'All Status'}
          </button>
        ))}
      </div>

      <div className="orders-summary-strip">
        <div className="orders-summary-card">
          <span>Orders</span>
          <strong>{totalCount}</strong>
        </div>
        <div className="orders-summary-card service">
          <span>Service Cost</span>
          <strong>{formatAmount(totals.serviceCost)}</strong>
        </div>
        <div className="orders-summary-card amount">
          <span>Total Amount</span>
          <strong>{formatAmount(totals.totalAmount)}</strong>
        </div>
      </div>

      <div className="content-card table-card">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Device</th>
              <th>Status</th>
              <th className="text-center">Service Cost</th>
              <th>Total Amount</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td><strong>{order.orderNumber}</strong></td>
                <td>
                  <div>{order.customerName}</div>
                  <small>{order.customerMobile}</small>
                </td>
                <td>
                  <div>{order.makeName}</div>
                  <small>{order.modelName}</small>
                </td>
                <td><span className="status-pill">{order.orderStatus}</span></td>
                <td className="text-center">{formatAmount(order.totalServiceCost)}</td>
                <td>{formatAmount(order.finalAmount)}</td>
                <td>{formatDate(order.orderDate)}</td>
                <td>
                  <button className="icon-action" onClick={() => navigate(`/orders/${order.id}`)} title="View order">
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="empty-state">Loading orders...</div>}
        {!loading && orders.length === 0 && <div className="empty-state">No orders found.</div>}
      </div>

      <div className="mobile-order-list">
        {orders.map((order) => (
          <button className="order-card" key={order.id} onClick={() => navigate(`/orders/${order.id}`)}>
            <div className="order-card-top">
              <div>
                <strong>{order.orderNumber}</strong>
                <span>{formatDate(order.orderDate)}</span>
              </div>
              <span className="status-pill">{order.orderStatus}</span>
            </div>
            <div className="order-card-device">{order.makeName} {order.modelName}</div>
            <div className="order-card-customer">
              <span>{order.customerName}</span>
              <span>{order.customerMobile}</span>
            </div>
            <div className="order-money-grid">
              <div className="money-tile service">
                <Wrench size={14} />
                <span>Service Cost</span>
                <strong>{formatAmount(order.totalServiceCost)}</strong>
              </div>
              <div className="money-tile amount">
                <IndianRupee size={14} />
                <span>Total Amount</span>
                <strong>{formatAmount(order.finalAmount)}</strong>
              </div>
            </div>
            <div className="order-card-footer">
              <span>View details</span>
            </div>
          </button>
        ))}
        {loading && <div className="empty-state mobile-empty">Loading orders...</div>}
        {!loading && orders.length === 0 && <div className="empty-state mobile-empty">No orders found.</div>}
      </div>

      <div className="pagination">
        <span>Showing {orders.length} of {totalCount}</span>
        <div>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
